import React, { useRef, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
} from '@mui/material';
import { Fingerprint } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useBiometric } from '../hooks/useBiometric';
import { useLanguage } from '../i18n/LanguageContext';

interface Props {
  open: boolean;
  title?: string;
  onSuccess: () => void;
  onClose: () => void;
}

export const AuthDialog: React.FC<Props> = ({ open, title, onSuccess, onClose }) => {
  const { t } = useLanguage();
  const { checkPassword, activeHouseId } = useAuth();
  const biometric = useBiometric(activeHouseId ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setPassword('');
    setError('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleConfirm = () => {
    if (!checkPassword(password)) {
      setError(t.loginWrongPassword);
      return;
    }
    reset();
    onSuccess();
  };

  const handleBiometricRetry = async () => {
    const ok = await biometric.authenticate();
    if (ok) {
      reset();
      onSuccess();
    } else {
      setError(t.biometricError);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth maxWidth="xs"
      TransitionProps={{ onEntered: () => inputRef.current?.focus() }}
    >
      <DialogTitle>{title ?? t.alarmAuthTitle}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          fullWidth type="password" label={t.loginPassword}
          inputRef={inputRef}
          value={password}
          onChange={e => { setPassword(e.target.value); setError(''); }}
          onKeyDown={e => e.key === 'Enter' && handleConfirm()}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t.nodeEditCancel}</Button>
        {biometric.isEnrolled && (
          <Button startIcon={<Fingerprint />} onClick={handleBiometricRetry}>
            {t.biometricLoginButton}
          </Button>
        )}
        <Button variant="contained" onClick={handleConfirm}>{t.alarmAuthConfirm}</Button>
      </DialogActions>
    </Dialog>
  );
};
